// Run a saved workflow on new files: one upload per source, column checks
// with a mapper for missing columns, then download every output.
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Select, Tag, Upload } from "antd";
import { CheckCircle2, Download, FileUp, Play } from "lucide-react";
import { toast } from "react-toastify";
// Roadmap 1 ran built-in steps with runWorkflow; Roadmap 2 runs code steps
// in the sandbox, through the data worker (files never load on the UI thread).
import { duplicateOutputIds, outputFileName } from "../../../engine";
import { countWorkflowRun } from "../../../api/workflowServices";
import { dataHost, downloadOutput } from "../data/dataHost";
import { fileRefsOf } from "../data/preview";
import { formatCount } from "../shared/display";
import { readWorkbook, useSourceChecks } from "../shared/sourceFiles";

// One upload slot per source: the columns it expects, then how the chosen
// file matches them (`check`, from useSourceChecks: missing columns can be
// mapped, type problems are warned about). Also used by the "Use this
// workflow" screen.
export const SourceSlot = ({ source, file, check, onFile, onSheet, onOverride }) => {
  const letters = check?.letters ?? [];
  const missing = check?.issues.filter((i) => i.level === "error") ?? [];

  return (
    <div className="rounded-lg border border-gray-200 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">{source.id.replace("source", "Source ")}</div>
          <div className="text-sm font-semibold">{source.label}</div>
        </div>
        <Upload accept=".csv,.xlsx,.txt" showUploadList={false} beforeUpload={(picked) => {
            onFile(picked);
            return false;
          }}>
          <Button icon={<FileUp size={14} />}>{file ? file.fileName : "Choose file"}</Button>
        </Upload>
      </div>
      {!file && source.columns.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <span className="text-[11px] text-gray-500">Expects:</span>
          {source.columns.map((column) => (
            <Tag key={column.name} className="!m-0 !text-[11px]">
              {column.letter ? `${column.letter} · ` : ""}
              {column.name}
              {column.type && column.type !== "text" ? ` (${column.type})` : ""}
            </Tag>
          ))}
        </div>
      )}
      {file?.sheets.length > 1 && (
        <Select size="small" className="w-full" value={file.sheetName} onChange={onSheet} options={file.sheets.map((s) => ({ value: s.name, label: `Sheet: ${s.name}` }))} />
      )}
      {check?.ok && (
        <div className="flex items-center gap-1 text-xs text-emerald-600">
          <CheckCircle2 size={14} /> All {source.columns.length} columns found · {formatCount(check.rowCount)} rows
        </div>
      )}
      {check?.issues.filter((i) => i.level !== "error").map((issue) => (
        <Alert key={issue.message} type={issue.level === "warning" ? "warning" : "info"} showIcon message={issue.message} className="!py-1 !text-xs" />
      ))}
      {missing.map((issue) => (
        <div key={issue.column} className="flex items-center gap-2 text-xs">
          <span className="flex-1 text-red-600">{issue.message}</span>
          <Select
            size="small"
            className="w-48"
            placeholder="Use column…"
            onChange={(letter) => onOverride(issue.column, letter)}
            options={letters.map((c) => ({ value: c.letter, label: c.header ? `${c.letter} · ${c.header}` : c.letter }))}
          />
        </div>
      ))}
    </div>
  );
};

const RunDialog = ({ open, onClose, workflow }) => {
  const [files, setFiles] = useState({});
  const [overrides, setOverrides] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const checks = useSourceChecks(workflow.sources, files, overrides);

  // Files and results of this dialog are freed in the worker when it closes.
  const loaded = useRef([]);
  useEffect(() => {
    if (open) {
      setFiles({});
      setOverrides({});
      setResult(null);
      return undefined;
    }
    if (loaded.current.length) dataHost.releaseFiles(loaded.current).catch(() => {});
    loaded.current = [];
    dataHost.dropScope("run").catch(() => {});
    return undefined;
  }, [open]);

  const pickFile = async (sourceId, picked) => {
    try {
      const workbook = await readWorkbook(picked);
      loaded.current.push(workbook.key);
      const source = workflow.sources.find((s) => s.id === sourceId);
      const sheetName = workbook.sheets.some((s) => s.name === source.sheetName) ? source.sheetName : workbook.sheets[0].name;
      setFiles((current) => ({ ...current, [sourceId]: { ...workbook, sheetName } }));
      setOverrides((current) => ({ ...current, [sourceId]: {} }));
      setResult(null);
    } catch (error) {
      toast.error(`Could not read ${picked.name}: ${error.message}`);
    }
  };

  const nameClash = duplicateOutputIds(workflow).size > 0;
  const ready = !nameClash && workflow.sources.every((source) => checks[source.id]?.ok);

  const run = async () => {
    setRunning(true);
    try {
      await countWorkflowRun();
    } catch (error) {
      toast.error(error?.message || "Run limit reached for your plan");
      setRunning(false);
      return;
    }
    // Roadmap 1:
    // setTimeout(() => {
    //   const cells = Object.fromEntries(Object.entries(files).map(([id, file]) => [id, cellsOf(file)]));
    //   setResult(runWorkflow(workflow, cells, { columnOverrides: overrides }));
    //   setRunning(false);
    // }, 30);
    const fileRefs = fileRefsOf(Object.fromEntries(Object.entries(files).map(([id, file]) => [id, { ...file, columnOverrides: overrides[id] }])));
    try {
      setResult(await dataHost.runFull(workflow, fileRefs));
    } catch (error) {
      toast.error(error?.message || "The workflow could not run");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Run “${workflow.name}” on new files`}
      width={720}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button key="run" type="primary" icon={<Play size={14} />} disabled={!ready} loading={running} onClick={run}>
          Run workflow
        </Button>,
      ]}
    >
      <div className="space-y-3">
        {nameClash && <Alert type="error" showIcon message="Two outputs have the same file name. Rename one before running." />}
        {workflow.sources.map((source) => (
          <SourceSlot
            key={source.id}
            source={source}
            file={files[source.id]}
            check={checks[source.id]}
            onFile={(picked) => pickFile(source.id, picked)}
            onSheet={(sheetName) => setFiles((current) => ({ ...current, [source.id]: { ...current[source.id], sheetName } }))}
            onOverride={(column, letter) =>
              setOverrides((current) => ({ ...current, [source.id]: { ...current[source.id], [column]: letter } }))
            }
          />
        ))}

        {result && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            {result.ok ? (
              <Alert type="success" showIcon message={`Done — ${result.results.length} steps ran.`} />
            ) : (
              <Alert type="error" showIcon message={[...result.errors, ...result.issues.filter((i) => i.level === "error").map((i) => i.message)].join(" ")} />
            )}
            <div className="flex flex-wrap gap-1">
              {result.results.map((r, i) => (
                <Tag key={r.stepId} color={r.status === "error" ? "red" : r.warnings.length ? "gold" : "default"}>
                  {i + 1}. {formatCount(r.rowsOut)} rows
                </Tag>
              ))}
            </div>
            {result.files.map((file) => (
              <div key={file.id} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
                <span className="text-sm font-medium">{outputFileName(file)}</span>
                <Button size="small" type="primary" icon={<Download size={14} />} onClick={() => downloadOutput("run", workflow.outputs.find((o) => o.id === file.id)).catch((error) => toast.error(error.message))}>
                  Download
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RunDialog;
